import { bbox, toMercator } from '@turf/turf'
import { contourApi, isEmptyApplicationsError } from 'api-validations/stitch'
import { useOrgCodeOrThrow } from 'shared-ui/active-operation'
import { useApiClient, useMutation, useQueryClient } from 'shared-ui/api-provider'
import { applicationsQueryConfig, useStitchRequestType } from 'shared-ui/field-data'
import { useLocalization } from 'shared-ui/localization'
import { type AsAppliedLayer, type BBox2D, useMapContext } from 'shared-ui/map'
import type { Stream } from 'shared-ui/operation-wide-data'
import { type BoundarySource, selectBoundaryLayer } from 'utils/boundary-autogen'

export enum BoundaryRequestKind {
  ActiveLayer = 'activeLayer',
  Passes = 'passes'
}

/**
 * What an autogenerated boundary outlines: the as-applied layer selected on the map, or a group of
 * the field's passes.
 */
export type BoundaryRequest =
  | { kind: BoundaryRequestKind.ActiveLayer; layer: AsAppliedLayer }
  | { kind: BoundaryRequestKind.Passes; source: BoundarySource<Stream> }

type ContourTarget = { streamId: string; applicationId: string; layerId: string }

const activeLayerTargets = ({
  streamId,
  applicationId,
  layerId,
  mergedStreamIds
}: AsAppliedLayer): ContourTarget[] =>
  [streamId, ...mergedStreamIds].map((id) => ({ streamId: id, applicationId, layerId }))

export const useGenerateBoundary = () => {
  const { map } = useMapContext()
  const { t } = useLocalization()

  const orgCode = useOrgCodeOrThrow()
  const apiClient = useApiClient()
  const queryClient = useQueryClient()
  const requestType = useStitchRequestType()

  const passTargets = async (source: BoundarySource<Stream>): Promise<ContourTarget[]> => {
    const targets = await Promise.all(
      source.streams.map(async ({ streamId }) => {
        const applications = await queryClient
          .query(applicationsQueryConfig({ orgCode, streamId, apiClient, requestType }))
          .catch((error: unknown) => {
            if (isEmptyApplicationsError(error)) return []
            throw error
          })
        return applications.length === 0 ? [] : [{ streamId, ...selectBoundaryLayer(applications) }]
      })
    )
    return targets.flat()
  }

  return useMutation({
    mutationFn: async (request: BoundaryRequest) => {
      const targets =
        request.kind === BoundaryRequestKind.ActiveLayer
          ? activeLayerTargets(request.layer)
          : await passTargets(request.source)
      if (targets.length === 0) {
        throw new Error(t(($) => $.planning.boundaryCreation.noCoverage))
      }

      const results = await Promise.all(
        targets.map((target) =>
          apiClient.callApi(contourApi, {
            requestType,
            orgCode,
            ...target,
            epsilon: 0.005,
            tolerance: 1 //0 is an option but I think its too spotty
          })
        )
      )

      return {
        type: results[0].type,
        features: results.flatMap(({ features }) => features)
      }
    },
    onSuccess: (result) => {
      map.drawTool.clear()
      map.drawTool.addFeatures(result)
      if (result.features.length > 0) map.zoomToExtent(bbox(toMercator(result)) as BBox2D)
    }
  })
}
